import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ProductRowCard } from './product-row-card';
import { ProductCardWithChinese } from './product-card-with-chinese';
import { Skeleton } from '@/components/ui/skeleton';
import { Package } from 'lucide-react';
import type { Product } from '@shared/schema';

interface ProductSectionProps {
  category: string;
  title: string;
  icon?: string;
  description?: string;
  bgColor?: string;
  compact?: boolean;
  columns?: number;
  useRowLayout?: boolean;
  chineseTitle?: string;
  accentColor?: 'emerald' | 'red' | 'blue' | 'amber';
}

const accentColors = {
  emerald: 'bg-emerald-600',
  red: 'bg-red-500',
  blue: 'bg-blue-500',
  amber: 'bg-amber-500',
};

export function ProductSection({
  category,
  title,
  icon,
  description,
  bgColor = 'bg-white',
  compact = false,
  columns = 3,
  useRowLayout = true,
  chineseTitle,
  accentColor = 'emerald'
}: ProductSectionProps) {
  const { data: products, isLoading, error } = useQuery<Product[]>({
    queryKey: ['/api/products/category', category],
    queryFn: async () => {
      const response = await fetch(`/api/products/category/${category}`);
      if (!response.ok) throw new Error('Failed to fetch products');
      return response.json();
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const gridCols = useRowLayout 
    ? 'grid-cols-1 gap-2'
    : compact 
      ? {
          2: 'grid-cols-2',
          3: 'grid-cols-2 sm:grid-cols-3',
          4: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
          5: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5',
          6: 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6'
        }[columns] || 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'
      : {
          2: 'grid-cols-2 md:grid-cols-2',
          3: 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3',
          4: 'grid-cols-2 md:grid-cols-2 lg:grid-cols-4',
          5: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
          6: 'grid-cols-3 md:grid-cols-4 lg:grid-cols-6'
        }[columns] || 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3';

  if (error) {
    return (
      <section className={`py-8 ${bgColor}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">{title}</h2>
            <p className="text-red-600">Failed to load products. Please try again later.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id={category} className={`relative py-6 ${bgColor} overflow-hidden`}>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="flex items-center gap-3">
              <div className={`w-1 h-8 ${accentColors[accentColor]} rounded-full`} />
              <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">
                {title}
              </h2>
              {chineseTitle && (
                <span className="text-lg text-gray-500 font-normal">{chineseTitle}</span>
              )}
            </div>
            {description && (
              <p className="text-gray-600 mt-2 ml-4">{description}</p>
            )}
          </motion.div>
        )}
        
        {isLoading ? (
          <div className={`grid ${gridCols} ${useRowLayout ? 'gap-2' : 'gap-4'}`}>
            {Array.from({ length: useRowLayout ? 6 : columns * 2 }).map((_, index) => (
              useRowLayout ? (
                <div key={index} className="bg-white rounded-xl shadow-lg p-4 animate-pulse">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-20 w-20 rounded-lg" />
                    <div className="flex-grow">
                      <Skeleton className="h-5 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2 mb-2" />
                      <Skeleton className="h-3 w-1/4" />
                    </div>
                    <div className="text-right">
                      <Skeleton className="h-6 w-20 mb-2" />
                      <Skeleton className="h-8 w-16" />
                    </div>
                  </div>
                </div>
              ) : (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
                  <Skeleton className={compact ? "h-32 w-full" : "h-48 w-full"} />
                  <div className={compact ? 'p-4' : 'p-6'}>
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-4" />
                    <Skeleton className="h-8 w-1/3 mb-4" />
                    <Skeleton className="h-2 w-full mb-4" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          <motion.div 
            className={`grid ${gridCols} gap-4 ${!useRowLayout ? 'items-stretch' : ''}`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {products?.map((product, index) => (
              <motion.div 
                key={product.id} 
                variants={itemVariants}
                custom={index}
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 50 }}
                viewport={{ once: true, amount: 0.1 }}
              >
                {useRowLayout ? (
                  <ProductRowCard product={product} />
                ) : (
                  <ProductCardWithChinese product={product} compact={compact} />
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        {products && products.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="inline-flex flex-col items-center gap-3 bg-gray-50 rounded-xl p-8">
              <Package className="w-12 h-12 text-gray-300" />
              <p className="text-gray-600 font-medium">No products available in this category yet.</p>
              <p className="text-gray-400 text-sm">Check back soon for updates!</p>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
